"use client";

import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const allTestimonials = [
  { id: 1, name: "Emeka Okonkwo", title: "Solar Distributor, Lagos", rating: 5, text: "I used to spend weeks chasing manufacturers. Now I find verified suppliers in minutes and my quote responses doubled within the first month." },
  { id: 2, name: "Adaeze Nwosu", title: "Homeowner, Abuja", rating: 5, text: "Booking a consultation was effortless. Someone actually called me back the same day. I had my system installed within three weeks." },
  { id: 3, name: "Chidi Okafor", title: "Solar Manufacturer, Port Harcourt", rating: 5, text: "The platform gave us visibility we never had before. We are now connected to distributors in five states we had never reached." },
  { id: 4, name: "Fatima Al-Hassan", title: "SME Owner, Kano", rating: 5, text: "Running a business without reliable power was killing us. Ren Power matched us with the right installer. Best decision we made this year." },
  { id: 5, name: "Tunde Balogun", title: "Solar Installer, Ibadan", rating: 5, text: "The product catalogue is genuinely stocked with quality gear. I have found better prices here than going through my old wholesale contacts." },
  { id: 6, name: "Ngozi Ezenwachi", title: "Recycling Coordinator, Enugu", rating: 4, text: "The recycling module connected us with pickup partners in record time. It is the kind of tool the green energy sector needed badly." },
  { id: 7, name: "Babatunde Adeyemi", title: "Real Estate Developer, Lagos", rating: 5, text: "We sourced panels for a 40-unit estate through the marketplace. The verification process gave us confidence to buy at volume." },
  { id: 8, name: "Grace Amaechi", title: "Homeowner, Portharcourt", rating: 5, text: "The solar calculator on the site told me exactly what capacity I needed. I went in informed and got a fair deal from a verified installer." },
  { id: 9, name: "Kola Adeleke", title: "Distributor, Oyo", rating: 4, text: "Managing inventory used to be a spreadsheet nightmare. The dashboard keeps everything in one place. My team actually uses it daily." },
  { id: 10, name: "Ifeoma Okeke", title: "Off-grid Community Coordinator", rating: 5, text: "We sourced backup power solutions for three rural communities. The consultation team understood our constraints and worked within them." },
];

const row1 = allTestimonials.slice(0, 5);
const row2 = allTestimonials.slice(5, 10);

function TestimonialCard({ item, dark }: { item: typeof allTestimonials[0]; dark?: boolean }) {
  return (
    <div
      className={`flex-shrink-0 w-72 sm:w-80 rounded-2xl p-5 border transition-all duration-300 ${
        dark
          ? "bg-[#002301] border-[#002301] text-white"
          : "bg-white border-gray-100 text-[#080A09] shadow-sm"
      }`}
    >
      <div className="flex gap-0.5 mb-3">
        {Array.from({ length: item.rating }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 fill-current ${dark ? "text-[#FFB600]" : "text-[#FFB600]"}`} />
        ))}
      </div>
      <p className={`text-sm leading-relaxed mb-4 ${dark ? "text-white/80" : "text-gray-600"}`}>
        "{item.text}"
      </p>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${dark ? "bg-white/20 text-white" : "bg-[#002301] text-white"}`}>
          {item.name.charAt(0)}
        </div>
        <div>
          <p className={`text-sm font-semibold ${dark ? "text-white" : "text-[#080A09]"}`}>{item.name}</p>
          <p className={`text-xs ${dark ? "text-white/50" : "text-gray-400"}`}>{item.title}</p>
        </div>
      </div>
    </div>
  );
}

function ScrollRow({ items, direction = "left", dark }: { items: typeof allTestimonials; direction?: "left" | "right"; dark?: boolean }) {
  const doubled = [...items, ...items];
  const animClass = direction === "left" ? "animate-scroll-left" : "animate-scroll-right";
  return (
    <div className="overflow-hidden w-full">
      <div
        className={`flex gap-4 w-max ${animClass} hover:[animation-play-state:paused]`}
      >
        {doubled.map((item, i) => (
          <TestimonialCard key={`${item.id}-${i}`} item={item} dark={dark && i % 3 === 0} />
        ))}
      </div>
    </div>
  );
}

export default function ReviewSection() {
  return (
    <section className="bg-[#F4F6F6] py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
        >
          Real people, real results
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
        >
          What Our Users Are Saying
        </motion.h2>
      </div>

      <div className="space-y-4 w-full">
        <ScrollRow items={row1} direction="left" dark />
        <ScrollRow items={row2} direction="right" />
      </div>
    </section>
  );
}
